import React, { useState, useMemo, useRef, useEffect } from "react";
import { MapPin, Calendar, ChevronDown, Check, Search, X } from "lucide-react";
import {
  LOCATION_SECTIONS,
  PHONE_CODES,
  DEFAULT_COUNTRY,
  NATIONALITY_GROUPS,
  REGION_I18N_KEY,
  type CountryCode,
} from "../../../lib/country-data";
import type { Location } from "@tabbio/types";
import {
  searchLocations,
  useDebouncedValue,
} from "../../../lib/location-source";
import { useTranslation } from "react-i18next";
import { useLocale } from "../../../hooks/useLocale";
import {
  EN_MONTHS_SHORT,
  EN_MONTHS_LONG,
  monthLabelsShort,
  monthLabelsLong,
  localizeDateLabel,
} from "../../../lib/month-names";

// ─── Shared Styles ──────────────────────────────────────────────────

const TRIGGER: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  height: 52,
  borderRadius: 9999,
  border: "1px solid var(--input-border)",
  padding: "0 22px",
  fontSize: 15,
  color: "var(--text-primary)",
  backgroundColor: "var(--input-bg)",
  boxShadow: "var(--gem-input-shadow)",
  fontFamily: "inherit",
  cursor: "pointer",
  boxSizing: "border-box" as const,
  textAlign: "start" as const,
};

// z-index must sit above WebDialog (1000) so the date / location pickers
// stay visible when opened from inside a form dialog: e.g. the experience
// edit modal. Picker overlay used to live at 200, which placed it BEHIND
// the dialog backdrop and made David's QA think the pickers themselves were
// missing (see SCRUM-274). Keep this comfortably above any dialog z-index.
const OVERLAY: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0,0,0,0.4)",
};

const PANEL: React.CSSProperties = {
  // Fixed height (clamped to 70vh on small viewports) so the modal does not
  // grow or shrink as search results come and go.
  width: "100%",
  maxWidth: 420,
  height: "min(560px, 70vh)",
  minHeight: 360,
  backgroundColor: "var(--input-bg)",
  borderRadius: 20,
  border: "1px solid var(--input-border)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  margin: 16,
};

// Content-sized variant for the generic option <select> replacement: short enum
// lists (gender, marital status…) should not force a 360px-tall modal, while a
// long searchable list (nationality) still scrolls within the 70vh cap.
const SELECT_PANEL: React.CSSProperties = {
  width: "100%",
  maxWidth: 380,
  maxHeight: "70vh",
  backgroundColor: "var(--input-bg)",
  borderRadius: 20,
  border: "1px solid var(--input-border)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  margin: 16,
};

const SEARCH_INPUT: React.CSSProperties = {
  flex: 1,
  border: "none",
  outline: "none",
  fontSize: 14,
  color: "var(--text-primary)",
  backgroundColor: "transparent",
  fontFamily: "inherit",
  padding: 0,
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 20px",
  cursor: "pointer",
  fontSize: 15,
  color: "var(--text-primary)",
  borderBottom: "1px solid var(--input-border)",
  fontFamily: "inherit",
  background: "none",
  border: "none",
  width: "100%",
  textAlign: "start" as const,
  transition: "background-color 100ms",
};

const SECTION_HEADER: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--text-tertiary)",
  padding: "14px 20px 6px",
};

// ─── Location Picker ────────────────────────────────────────────────

interface WebLocationPickerProps {
  value: string;
  onSelect: (v: string) => void;
  placeholder?: string;
  label?: string;
  includeRemote?: boolean;
}

export function WebLocationPicker({
  value,
  onSelect,
  placeholder,
  label,
  includeRemote = true,
}: WebLocationPickerProps) {
  const { t } = useTranslation("common");
  // Localize the curated section headers; section data + stored value stay English.
  const regionLabel = (title: string) =>
    title
      ? t(`location.regions.${REGION_I18N_KEY[title] ?? title}`, {
          defaultValue: title,
        })
      : "";
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 250);
  const abortRef = useRef<AbortController | null>(null);

  const userTyped = search.trim().length > 0;

  useEffect(() => {
    const q = debouncedSearch.trim();
    abortRef.current?.abort();
    // Clear results immediately when the debounced query changes so the user
    // never sees rows from a previous query while the new fetch is in-flight.
    setResults([]);
    if (q.length < 2) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    searchLocations(q, {
      signal: controller.signal,
      includeCountries: true,
    })
      .then((res) => {
        if (!controller.signal.aborted) {
          setResults(res);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setResults([]);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [debouncedSearch]);

  const isStale = debouncedSearch !== search;
  const showLoadingRow = userTyped && (loading || isStale);
  const visibleResults = userTyped && !isStale && !loading ? results : [];
  const showNoResultsRow =
    userTyped && !loading && !isStale && results.length === 0;

  return (
    <>
      {label && (
        <label
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-secondary)",
            marginBottom: 8,
            display: "block",
          }}
        >
          {label}
        </label>
      )}
      <button
        onClick={() => setOpen(true)}
        style={{
          ...TRIGGER,
          color: value ? "var(--text-primary)" : "var(--text-tertiary)",
        }}
      >
        <MapPin size={15} color="var(--text-tertiary)" />
        <span style={{ flex: 1 }}>
          {value || placeholder || t("location.selectLocation")}
        </span>
        <ChevronDown size={14} color="var(--text-tertiary)" />
      </button>

      {open && (
        <div
          style={OVERLAY}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
              setSearch("");
            }
          }}
        >
          <div style={PANEL}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                borderBottom: "1px solid var(--input-border)",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                {t("location.title")}
              </span>
              <button
                onClick={() => {
                  setOpen(false);
                  setSearch("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={16} color="var(--text-secondary)" />
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: "10px 16px",
                padding: "0 12px",
                height: 44,
                borderRadius: 9999,
                border: "1px solid var(--input-border)",
                backgroundColor: "var(--input-bg)",
              }}
            >
              <Search size={14} color="var(--text-tertiary)" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("location.search")}
                autoFocus
                style={SEARCH_INPUT}
              />
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {userTyped ? (
                <>
                  {visibleResults.map((r, i) => (
                    <button
                      key={`${r.displayName}-${i}`}
                      onClick={() => {
                        onSelect(r.displayName);
                        setOpen(false);
                        setSearch("");
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          "rgba(0,0,0,0.04)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          "transparent";
                      }}
                      style={ROW}
                    >
                      <span
                        style={{
                          fontWeight: r.displayName === value ? 600 : 400,
                        }}
                      >
                        {r.displayName}
                      </span>
                      {r.displayName === value && (
                        <Check
                          size={16}
                          color="var(--text-primary)"
                          strokeWidth={2.5}
                        />
                      )}
                    </button>
                  ))}
                  {showLoadingRow && (
                    <div
                      style={{
                        ...ROW,
                        color: "var(--text-secondary)",
                        cursor: "default",
                      }}
                    >
                      <span>{t("location.searching")}</span>
                    </div>
                  )}
                  {showNoResultsRow && (
                    <div
                      style={{
                        ...ROW,
                        color: "var(--text-tertiary)",
                        cursor: "default",
                      }}
                    >
                      <span>{t("location.noMatches")}</span>
                    </div>
                  )}
                </>
              ) : (
                LOCATION_SECTIONS.filter(
                  (section) => includeRemote || section.title !== "Remote",
                ).map((section) => (
                  <React.Fragment key={section.title}>
                    <div style={SECTION_HEADER}>
                      {regionLabel(section.title)}
                    </div>
                    {section.data.map((loc) => (
                      <button
                        key={loc}
                        onClick={() => {
                          onSelect(loc);
                          setOpen(false);
                          setSearch("");
                        }}
                        onMouseEnter={(e) => {
                          (
                            e.currentTarget as HTMLElement
                          ).style.backgroundColor = "rgba(0,0,0,0.04)";
                        }}
                        onMouseLeave={(e) => {
                          (
                            e.currentTarget as HTMLElement
                          ).style.backgroundColor = "transparent";
                        }}
                        style={ROW}
                      >
                        <span style={{ fontWeight: loc === value ? 600 : 400 }}>
                          {loc}
                        </span>
                        {loc === value && (
                          <Check
                            size={16}
                            color="var(--text-primary)"
                            strokeWidth={2.5}
                          />
                        )}
                      </button>
                    ))}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Date Picker ────────────────────────────────────────────────────

// The selected value is STORED in English ("Jun 2026" / "12 June 1998") to match
// the native DatePickerField and keep parsePickerDate/parsePeriodString happy.
// Month grids show locale-aware labels (monthLabelsShort/Long) while the stored
// value is built from the English arrays; the trigger label is localized for
// display via localizeDateLabel. See month-names.ts.
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const currentYear = new Date().getFullYear();
const MIN_YEAR = 1960;
const MAX_YEAR = currentYear + 5;

function parseYearFromValue(value: string): number | null {
  const m = value.match(/(\d{4})/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  return y >= MIN_YEAR && y <= MAX_YEAR ? y : null;
}

// Day-granularity value parser. Accepts the native "12 March 1998" format and
// legacy ISO "1998-03-12" (what the old web <input type="date"> stored) so an
// existing date pre-selects correctly when the picker re-opens.
function parseDayValue(
  value: string,
): { year: number; month: number; day: number } | null {
  if (!value) return null;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const year = parseInt(iso[1], 10);
    const month = parseInt(iso[2], 10) - 1;
    const day = parseInt(iso[3], 10);
    if (month >= 0 && month < 12 && day >= 1 && day <= 31)
      return { year, month, day };
  }
  const parts = value.trim().split(/\s+/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = EN_MONTHS_LONG.indexOf(parts[1]);
    const year = parseInt(parts[2], 10);
    if (day >= 1 && day <= 31 && month >= 0 && !isNaN(year))
      return { year, month, day };
  }
  return null;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

interface WebDatePickerProps {
  value: string;
  onSelect: (v: string) => void;
  placeholder?: string;
  label?: string;
  /** 'month' (default) → "Jan 2024"; 'day' → "12 March 1998" (e.g. Date of Birth). */
  granularity?: "month" | "day";
}

export function WebDatePicker({
  value,
  onSelect,
  placeholder,
  label,
  granularity = "month",
}: WebDatePickerProps) {
  const { locale } = useLocale();
  // Display labels follow the active locale; the stored value is always built
  // from the English arrays so downstream parsers keep working.
  const monthShort = monthLabelsShort(locale);
  const monthLong = monthLabelsLong(locale);
  const isDay = granularity === "day";
  // DOB can't be in the future, so day mode caps the year at the current one.
  const maxYear = isDay ? currentYear : MAX_YEAR;
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(
    () =>
      (isDay ? parseDayValue(value)?.year : null) ??
      parseYearFromValue(value) ??
      currentYear,
  );
  // Day mode is a year → month → day flow; `month`/`day` are null until chosen.
  const [month, setMonth] = useState<number | null>(() =>
    isDay ? (parseDayValue(value)?.month ?? null) : null,
  );
  const [day, setDay] = useState<number | null>(() =>
    isDay ? (parseDayValue(value)?.day ?? null) : null,
  );
  const [yearListOpen, setYearListOpen] = useState(false);
  const yearListRef = useRef<HTMLDivElement | null>(null);

  // Re-sync from value when closed (e.g. picker reused for end date, or external change).
  useEffect(() => {
    if (open) return;
    if (isDay) {
      const parsed = parseDayValue(value);
      if (parsed) {
        setYear(parsed.year);
        setMonth(parsed.month);
        setDay(parsed.day);
      } else {
        setMonth(null);
        setDay(null);
        const y = parseYearFromValue(value);
        if (y !== null) setYear(y);
      }
    } else {
      const parsed = parseYearFromValue(value);
      if (parsed !== null) setYear(parsed);
    }
    setYearListOpen(false);
  }, [open, value, isDay]);

  // Scroll selected year into view when list opens.
  useEffect(() => {
    if (!yearListOpen) return;
    const node = yearListRef.current?.querySelector<HTMLElement>(
      '[data-selected="true"]',
    );
    node?.scrollIntoView({ block: "center" });
  }, [yearListOpen]);

  const yearOptions = useMemo(() => {
    const out: number[] = [];
    for (let y = maxYear; y >= MIN_YEAR; y--) out.push(y);
    return out;
  }, [maxYear]);

  // Midnight today, for disabling future days in day mode.
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  return (
    <>
      {label && (
        <label
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-secondary)",
            marginBottom: 8,
            display: "block",
          }}
        >
          {label}
        </label>
      )}
      <button
        onClick={() => setOpen(true)}
        style={{
          ...TRIGGER,
          color: value ? "var(--text-primary)" : "var(--text-tertiary)",
        }}
      >
        <Calendar size={15} color="var(--text-tertiary)" />
        <span style={{ flex: 1 }}>
          {value
            ? localizeDateLabel(value, locale)
            : placeholder || "Select date"}
        </span>
        <ChevronDown size={14} color="var(--text-tertiary)" />
      </button>

      {open && (
        <div
          style={OVERLAY}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          {/* Day mode sizes to content (year → month → day); month mode keeps the original fixed panel. */}
          <div
            style={{
              ...PANEL,
              maxWidth: 360,
              ...(isDay ? { height: "auto", minHeight: 0 } : {}),
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                borderBottom: "1px solid var(--input-border)",
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                Select date
              </span>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={16} color="var(--text-secondary)" />
              </button>
            </div>

            {/* Year row */}
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                padding: "16px 0",
              }}
            >
              <button
                onClick={() => setYear((y) => Math.max(MIN_YEAR, y - 1))}
                disabled={year <= MIN_YEAR}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: "var(--hover-strong)",
                  border: "none",
                  cursor: year <= MIN_YEAR ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  color: "var(--text-primary)",
                  opacity: year <= MIN_YEAR ? 0.3 : 1,
                }}
              >
                ‹
              </button>
              <button
                type="button"
                onClick={() => setYearListOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={yearListOpen}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 10,
                  backgroundColor: yearListOpen
                    ? "var(--hover-strong)"
                    : "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background-color 100ms",
                }}
              >
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "var(--text-primary)",
                    minWidth: 50,
                    textAlign: "center",
                  }}
                >
                  {year}
                </span>
                <ChevronDown
                  size={14}
                  color="var(--text-tertiary)"
                  style={{
                    transform: yearListOpen ? "rotate(180deg)" : "none",
                    transition: "transform 150ms",
                  }}
                />
              </button>
              <button
                onClick={() => setYear((y) => Math.min(maxYear, y + 1))}
                disabled={year >= maxYear}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: "var(--hover-strong)",
                  border: "none",
                  cursor: year >= maxYear ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  color: "var(--text-primary)",
                  opacity: year >= maxYear ? 0.3 : 1,
                }}
              >
                ›
              </button>

              {yearListOpen && (
                <div
                  ref={yearListRef}
                  role="listbox"
                  aria-label="Year"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginTop: 4,
                    width: 160,
                    maxHeight: 240,
                    overflowY: "auto",
                    backgroundColor: "var(--input-bg)",
                    border: "1px solid var(--input-border)",
                    borderRadius: 12,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                    zIndex: 10,
                    padding: 4,
                  }}
                >
                  {yearOptions.map((y) => {
                    const isSelected = y === year;
                    return (
                      <button
                        key={y}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        data-selected={isSelected || undefined}
                        onClick={() => {
                          setYear(y);
                          setYearListOpen(false);
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected)
                            (
                              e.currentTarget as HTMLElement
                            ).style.backgroundColor = "var(--hover-strong)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected)
                            (
                              e.currentTarget as HTMLElement
                            ).style.backgroundColor = "transparent";
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: 8,
                          border: "none",
                          textAlign: "center",
                          fontSize: 15,
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected
                            ? "var(--tabbio-button-primary-text)"
                            : "var(--text-primary)",
                          backgroundColor: isSelected
                            ? "var(--tabbio-button-primary-bg)"
                            : "transparent",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          transition: "background-color 100ms",
                        }}
                      >
                        {y}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {isDay ? (
              month === null ? (
                /* Day mode: step 1: pick a month (does not close the picker). */
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 8,
                    padding: "0 20px 16px",
                  }}
                >
                  {monthShort.map((m, idx) => {
                    const isSelected = month === idx;
                    return (
                      <button
                        key={idx}
                        onClick={() => setMonth(idx)}
                        onMouseEnter={(e) => {
                          if (!isSelected)
                            (
                              e.currentTarget as HTMLElement
                            ).style.backgroundColor = "var(--hover-strong)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected)
                            (
                              e.currentTarget as HTMLElement
                            ).style.backgroundColor = "transparent";
                        }}
                        style={{
                          padding: "10px 0",
                          borderRadius: 10,
                          border: "none",
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected
                            ? "var(--tabbio-button-primary-text)"
                            : "var(--text-primary)",
                          backgroundColor: isSelected
                            ? "var(--tabbio-button-primary-bg)"
                            : "transparent",
                          fontFamily: "inherit",
                          transition: "background-color 100ms",
                        }}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Day mode: step 2: pick a day within the chosen month. */
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 20px 8px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setMonth(null)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "4px 10px",
                        borderRadius: 10,
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--text-primary)",
                        fontFamily: "inherit",
                      }}
                    >
                      <span style={{ color: "var(--text-tertiary)" }}>‹</span>{" "}
                      {monthLong[month]}
                    </button>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      gap: 4,
                      padding: "0 16px",
                    }}
                  >
                    {WEEKDAYS.map((w) => (
                      <div
                        key={w}
                        style={{
                          textAlign: "center",
                          fontSize: 11,
                          fontWeight: 500,
                          color: "var(--text-tertiary)",
                          padding: "2px 0",
                        }}
                      >
                        {w}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      gap: 4,
                      padding: "4px 16px 16px",
                    }}
                  >
                    {Array.from({
                      length: new Date(year, month, 1).getDay(),
                    }).map((_, i) => (
                      <div key={`blank-${i}`} />
                    ))}
                    {Array.from({ length: daysInMonth(year, month) }).map(
                      (_, i) => {
                        const d = i + 1;
                        const isSelected = day === d;
                        const disabled = new Date(year, month, d) > today;
                        return (
                          <button
                            key={d}
                            disabled={disabled}
                            onClick={() => {
                              onSelect(`${d} ${EN_MONTHS_LONG[month]} ${year}`);
                              setDay(d);
                              setOpen(false);
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected && !disabled)
                                (
                                  e.currentTarget as HTMLElement
                                ).style.backgroundColor = "var(--hover-strong)";
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected)
                                (
                                  e.currentTarget as HTMLElement
                                ).style.backgroundColor = "transparent";
                            }}
                            style={{
                              aspectRatio: "1 / 1",
                              borderRadius: 9999,
                              border: "none",
                              cursor: disabled ? "default" : "pointer",
                              fontSize: 14,
                              fontWeight: isSelected ? 600 : 400,
                              color: isSelected
                                ? "var(--tabbio-button-primary-text)"
                                : "var(--text-primary)",
                              backgroundColor: isSelected
                                ? "var(--tabbio-button-primary-bg)"
                                : "transparent",
                              opacity: disabled ? 0.3 : 1,
                              fontFamily: "inherit",
                              transition: "background-color 100ms",
                            }}
                          >
                            {d}
                          </button>
                        );
                      },
                    )}
                  </div>
                </>
              )
            ) : (
              <>
                {/* Month grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 8,
                    padding: "0 20px 12px",
                  }}
                >
                  {EN_MONTHS_SHORT.map((mEn, idx) => {
                    const val = `${mEn} ${year}`;
                    const isSelected = value === val;
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          onSelect(val);
                          setOpen(false);
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected)
                            (
                              e.currentTarget as HTMLElement
                            ).style.backgroundColor = "var(--hover-strong)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected)
                            (
                              e.currentTarget as HTMLElement
                            ).style.backgroundColor = "transparent";
                        }}
                        style={{
                          padding: "10px 0",
                          borderRadius: 10,
                          border: "none",
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected
                            ? "var(--tabbio-button-primary-text)"
                            : "var(--text-primary)",
                          backgroundColor: isSelected
                            ? "var(--tabbio-button-primary-bg)"
                            : "transparent",
                          fontFamily: "inherit",
                          transition: "background-color 100ms",
                        }}
                      >
                        {monthShort[idx]}
                      </button>
                    );
                  })}
                </div>

                {/* Year only */}
                <div style={{ padding: "0 20px 16px" }}>
                  <button
                    onClick={() => {
                      onSelect(String(year));
                      setOpen(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px 0",
                      borderRadius: 10,
                      border: "1px solid var(--input-border)",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "var(--text-secondary)",
                      fontFamily: "inherit",
                    }}
                  >
                    Just select {year}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Generic Select (native <select> replacement) ───────────────────

interface WebSelectOption {
  value: string;
  label: string;
}
interface WebSelectProps {
  value: string;
  onSelect: (value: string) => void;
  options: WebSelectOption[];
  placeholder?: string;
  label?: string;
  /** Modal heading; falls back to `label` then "Select". */
  title?: string;
  /** Show a search box (use for long lists like nationality). */
  searchable?: boolean;
  searchPlaceholder?: string;
}

export function WebSelect({
  value,
  onSelect,
  options,
  placeholder,
  label,
  title,
  searchable = false,
  searchPlaceholder,
}: WebSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedLabel = options.find((o) => o.value === value)?.label ?? "";

  const filtered = useMemo(() => {
    if (!searchable) return options;
    const q = search.toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search, searchable]);

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <>
      {label && (
        <label
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-secondary)",
            marginBottom: 8,
            display: "block",
          }}
        >
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          ...TRIGGER,
          color: selectedLabel ? "var(--text-primary)" : "var(--text-tertiary)",
        }}
      >
        <span style={{ flex: 1 }}>
          {selectedLabel || placeholder || "Select"}
        </span>
        <ChevronDown size={14} color="var(--text-tertiary)" />
      </button>

      {open && (
        <div
          style={OVERLAY}
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div style={SELECT_PANEL}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                borderBottom: "1px solid var(--input-border)",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                {title || label || "Select"}
              </span>
              <button
                onClick={close}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={16} color="var(--text-secondary)" />
              </button>
            </div>
            {searchable && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  margin: "10px 16px",
                  padding: "0 12px",
                  height: 44,
                  borderRadius: 9999,
                  border: "1px solid var(--input-border)",
                  backgroundColor: "var(--input-bg)",
                  flexShrink: 0,
                }}
              >
                <Search size={14} color="var(--text-tertiary)" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder || "Search..."}
                  autoFocus
                  style={SEARCH_INPUT}
                />
              </div>
            )}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filtered.map((o) => {
                const isSelected = o.value === value;
                return (
                  <button
                    key={o.value}
                    onClick={() => {
                      onSelect(o.value);
                      close();
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "rgba(0,0,0,0.04)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "transparent";
                    }}
                    style={ROW}
                  >
                    <span style={{ fontWeight: isSelected ? 600 : 400 }}>
                      {o.label}
                    </span>
                    {isSelected && (
                      <Check
                        size={16}
                        color="var(--text-primary)"
                        strokeWidth={2.5}
                      />
                    )}
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div
                  style={{
                    ...ROW,
                    color: "var(--text-tertiary)",
                    cursor: "default",
                  }}
                >
                  <span>No matches</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Phone Input ────────────────────────────────────────────────────

const COUNTRY_CODES = PHONE_CODES;

interface WebPhoneInputProps {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  disabled?: boolean;
  borderColor?: string;
  inputId?: string;
  describedBy?: string;
  required?: boolean;
  invalid?: boolean;
  /**
   * Optional ISO 3166-1 alpha-2 country code that sets the dial-code default.
   * When this prop changes, the dial code updates automatically: until the
   * user manually picks a country, after which manual choice wins.
   */
  countryCode?: string | null;
  /**
   * Reports the active ISO country (detected, prop-synced or user-picked) so
   * callers can run per-country validation: mirrors the native PhoneInput's
   * `onChangeCountry`.
   */
  onCountryChange?: (isoCountryCode: string) => void;
}

export function WebPhoneInput({
  value,
  onChange,
  label,
  disabled = false,
  borderColor,
  inputId,
  describedBy,
  required = false,
  invalid = false,
  countryCode,
  onCountryChange,
}: WebPhoneInputProps) {
  const { isRTL } = useLocale();
  // Auto-detect country from phone value by matching dial codes (longest match first)
  const detectCountry = (phone: string): CountryCode => {
    if (!phone || !phone.startsWith("+")) return DEFAULT_COUNTRY;
    const sorted = [...PHONE_CODES].sort(
      (a, b) => b.dial.length - a.dial.length,
    );
    for (const c of sorted) {
      if (phone.startsWith(c.dial)) return c;
    }
    return DEFAULT_COUNTRY;
  };

  const [country, setCountry] = useState<CountryCode>(() => {
    if (value && value.startsWith("+")) return detectCountry(value);
    if (countryCode) {
      const byProp = PHONE_CODES.find(
        (c) => c.code === countryCode.toUpperCase(),
      );
      if (byProp) return byProp;
    }
    return DEFAULT_COUNTRY;
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const userOverrideRef = useRef(false);

  // Re-detect country when value changes externally (e.g. from CV parse)
  useEffect(() => {
    if (value && value.startsWith("+")) {
      const detected = detectCountry(value);
      if (detected.dial !== country.dial) setCountry(detected);
    }
  }, [country.dial, value]);

  // Sync from `countryCode` prop (e.g. nationality changed) until user overrides.
  useEffect(() => {
    if (!countryCode || userOverrideRef.current) return;
    const next = PHONE_CODES.find((c) => c.code === countryCode.toUpperCase());
    if (!next || next.code === country.code) return;

    setCountry(next);

    const local = value.startsWith(country.dial)
      ? value.slice(country.dial.length).trim()
      : value.replace(/^\+\d+\s*/, "").trim();
    if (local) onChange(`${next.dial} ${local}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  // Surface the active country to the caller whenever it settles (initial
  // detect, prop sync, or manual pick) for per-country validation.
  useEffect(() => {
    onCountryChange?.(country.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country.code]);

  const localNumber = value.startsWith(country.dial)
    ? value.slice(country.dial.length).trim()
    : value.replace(/^\+\d+\s*/, "");

  const handleLocalChange = (text: string) => {
    const cleaned = text.replace(/\D/g, "");
    onChange(cleaned ? `${country.dial} ${cleaned}` : "");
  };

  const handleCountrySelect = (c: CountryCode) => {
    userOverrideRef.current = true;
    setCountry(c);
    setOpen(false);
    setSearch("");
    if (localNumber) onChange(`${c.dial} ${localNumber}`);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return COUNTRY_CODES;
    return COUNTRY_CODES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q),
    );
  }, [search]);

  return (
    <>
      {label && (
        <label
          htmlFor={inputId}
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-secondary)",
            marginBottom: 8,
            display: "block",
          }}
        >
          {label}
        </label>
      )}
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          display: "flex",
          borderRadius: 9999,
          border: "1px solid var(--input-border)",
          overflow: "hidden",
          height: 52,
          backgroundColor: "var(--input-bg)",
          boxShadow: "var(--gem-input-shadow)",
        }}
      >
        <button
          type="button"
          onClick={() => !disabled && setOpen(true)}
          disabled={disabled}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            paddingBlock: 0,
            paddingInlineStart: 18,
            paddingInlineEnd: 14,
            background: "transparent",
            border: 0,
            cursor: disabled ? "default" : "pointer",
            fontFamily: "inherit",
            flexShrink: 0,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <span style={{ fontSize: 16 }}>{country.flag}</span>
          <span
            dir="ltr"
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: "var(--text-primary)",
            }}
          >
            {country.dial}
          </span>
          <ChevronDown size={12} color="var(--text-tertiary)" />
        </button>
        <input
          id={inputId}
          value={localNumber}
          onChange={(e) => handleLocalChange(e.target.value)}
          placeholder="50 123 4567"
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="tel-national"
          disabled={disabled}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            padding: "0 16px",
            fontSize: 15,
            color: "var(--text-primary)",
            fontFamily: "inherit",
            backgroundColor: "transparent",
            direction: "ltr",
            textAlign: "left",
          }}
        />
      </div>

      {open && (
        <div
          style={OVERLAY}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
              setSearch("");
            }
          }}
        >
          <div style={{ ...PANEL, maxWidth: 380 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                borderBottom: "1px solid var(--input-border)",
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                Country code
              </span>
              <button
                onClick={() => {
                  setOpen(false);
                  setSearch("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={16} color="var(--text-secondary)" />
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: "10px 16px",
                padding: "0 12px",
                height: 44,
                borderRadius: 9999,
                border: "1px solid var(--input-border)",
                backgroundColor: "var(--input-bg)",
              }}
            >
              <Search size={14} color="var(--text-tertiary)" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                autoFocus
                style={SEARCH_INPUT}
              />
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filtered.map((c) => (
                <button
                  key={c.code}
                  onClick={() => handleCountrySelect(c)}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      "rgba(0,0,0,0.04)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      "transparent";
                  }}
                  style={{ ...ROW, gap: 10 }}
                >
                  <span style={{ fontSize: 18 }}>{c.flag}</span>
                  <span
                    style={{
                      flex: 1,
                      fontWeight: c.code === country.code ? 600 : 400,
                    }}
                  >
                    {c.name}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      color: "var(--text-secondary)",
                      fontWeight: 500,
                    }}
                  >
                    {c.dial}
                  </span>
                  {c.code === country.code && (
                    <Check
                      size={16}
                      color="var(--text-primary)"
                      strokeWidth={2.5}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Nationality Picker ─────────────────────────────────────────────

// Nationality data from shared country-data.ts

interface WebNationalityPickerProps {
  value: string;
  onSelect: (v: string) => void;
  placeholder?: string;
  label?: string;
}

export function WebNationalityPicker({
  value,
  onSelect,
  placeholder,
  label,
}: WebNationalityPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return NATIONALITY_GROUPS;
    return NATIONALITY_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter((n) => n.toLowerCase().includes(q)),
    })).filter((g) => g.items.length > 0);
  }, [search]);

  return (
    <>
      {label && (
        <label
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-secondary)",
            marginBottom: 8,
            display: "block",
          }}
        >
          {label}
        </label>
      )}
      <button
        onClick={() => setOpen(true)}
        style={{
          ...TRIGGER,
          color: value ? "var(--text-primary)" : "var(--text-tertiary)",
        }}
      >
        <span style={{ flex: 1 }}>
          {value || placeholder || "Select nationality"}
        </span>
        <ChevronDown size={14} color="var(--text-tertiary)" />
      </button>

      {open && (
        <div
          style={OVERLAY}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
              setSearch("");
            }
          }}
        >
          <div style={PANEL}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                borderBottom: "1px solid var(--input-border)",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                Nationality
              </span>
              <button
                onClick={() => {
                  setOpen(false);
                  setSearch("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={16} color="var(--text-secondary)" />
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: "10px 16px",
                padding: "0 12px",
                height: 44,
                borderRadius: 9999,
                border: "1px solid var(--input-border)",
                backgroundColor: "var(--input-bg)",
              }}
            >
              <Search size={14} color="var(--text-tertiary)" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                autoFocus
                style={SEARCH_INPUT}
              />
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filtered.map((group) => (
                <React.Fragment key={group.label}>
                  <div style={SECTION_HEADER}>{group.label}</div>
                  {group.items.map((nat) => (
                    <button
                      key={nat}
                      onClick={() => {
                        onSelect(nat);
                        setOpen(false);
                        setSearch("");
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          "rgba(0,0,0,0.04)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          "transparent";
                      }}
                      style={ROW}
                    >
                      <span style={{ fontWeight: nat === value ? 600 : 400 }}>
                        {nat}
                      </span>
                      {nat === value && (
                        <Check
                          size={16}
                          color="var(--text-primary)"
                          strokeWidth={2.5}
                        />
                      )}
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
